/**
 * offlineQueue.js — IndexedDB-backed mutation queue for offline support.
 *
 * When a write (POST/PUT/PATCH/DELETE) fails due to network issues,
 * it's stored here. On reconnection, queued mutations are replayed
 * in order. Each write includes a client timestamp for conflict resolution.
 *
 * Conflict resolution: Last-Write-Wins based on client timestamp.
 * The backend flags records updated from offline as "mobile_conflict".
 */

const DB_NAME    = 'gd-offline';
const DB_VERSION = 2;   // must match offlineDB.js — both share the same database
const STORE      = 'queue';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      // v1 store — mutation queue
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('by_status', 'status');
      }
      // v2 stores — created by offlineDB.js; ensure they exist here too
      if (!db.objectStoreNames.contains('apiCache')) {
        const cache = db.createObjectStore('apiCache', { keyPath: 'url' });
        cache.createIndex('by_timestamp', 'timestamp');
      }
      if (!db.objectStoreNames.contains('authCache')) {
        db.createObjectStore('authCache', { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function queueMutation({ method, url, body, description = '' }) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).add({
      method,
      url,
      body,
      description,
      status: 'pending',
      client_timestamp: new Date().toISOString(),
      created_at: new Date().toISOString(),
    });
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function getPendingMutations() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).index('by_status').getAll('pending');
    req.onsuccess = () => resolve(req.result || []);
    req.onerror   = () => reject(req.error);
  });
}

export async function markMutationDone(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (record) {
        record.status = 'completed';
        record.completed_at = new Date().toISOString();
        store.put(record).onsuccess = resolve;
      } else resolve();
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

export async function markMutationFailed(id, error) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (record) {
        record.status = 'failed';
        record.error  = String(error);
        store.put(record).onsuccess = resolve;
      } else resolve();
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

export async function getPendingCount() {
  const pending = await getPendingMutations();
  return pending.length;
}

export async function clearCompleted() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const req = store.index('by_status').openCursor(IDBKeyRange.only('completed'));
    req.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) { cursor.delete(); cursor.continue(); }
      else resolve();
    };
    req.onerror = () => reject(req.error);
  });
}
